import * as React from 'react';
import { useDepots } from '../../hooks/useDepots';
import { useBacs } from '../../hooks/useBacs';
import { usePlanning } from '../../hooks/usePlanning';
import { useCapacites } from '../../hooks/useCapacites';
import { useAuth } from '../../core/context/ProvidersContext';
import { DepotCard } from './DepotCard';
import { DepotDetail } from './DepotDetail';
import { getCurrentMonthISO } from '../shared/MonthUtils';
import styles from './DepotsView.module.scss';

export const DepotsView: React.FC = () => {
  const { depots, loading: depotsLoading, refresh: refreshDepots } = useDepots();
  const { bacs, loading: bacsLoading, refresh: refreshBacs } = useBacs();
  const { entries, refresh: refreshPlanning } = usePlanning();
  const { capacites, refresh: refreshCapacites } = useCapacites();
  const { hasRole } = useAuth();
  const isAdmin = hasRole('admin');

  const [selectedDepotId, setSelectedDepotId] = React.useState<number | null>(null);
  const [search, setSearch] = React.useState('');

  const currentMonth = React.useMemo(() => getCurrentMonthISO().substring(0, 7), []);

  const depotStats = React.useMemo(() => {
    return depots.map(d => {
      const depotBacs = bacs.filter(b => b.DepotId === d.Id);
      const volumeTotal = depotBacs.reduce((s, b) => s + (b.VolumeUtile || 0), 0);
      const bacIds = depotBacs.map(b => b.Id);
      const monthEntries = entries.filter(
        e => bacIds.indexOf(e.BacId) >= 0 && e.Mois.substring(0, 7) === currentMonth
      );
      const indispo = monthEntries.filter(
        e => e.TypeContenu === 'Intervention' || e.TypeContenu === 'Transition'
      ).length;
      const currentProducts = Array.from(
        new Set(
          monthEntries
            .filter(e => e.TypeContenu === 'Produit' && e.Contenu)
            .map(e => e.Contenu)
        )
      );
      const withRestriction = depotBacs.filter(
        b => b.RestrictionClients !== null && b.RestrictionClients !== 0
      ).length;

      return {
        depot: d,
        bacsCount: depotBacs.length,
        volumeTotal,
        indispoCount: indispo,
        currentProducts,
        withRestriction,
      };
    });
  }, [depots, bacs, entries, currentMonth]);

  const filteredStats = React.useMemo(() => {
    if (!search) return depotStats;
    const q = search.toLowerCase();
    return depotStats.filter(s => s.depot.Title.toLowerCase().indexOf(q) >= 0);
  }, [depotStats, search]);

  const handleAfterEdit = (): void => {
    refreshDepots();
    refreshBacs();
    refreshPlanning();
    refreshCapacites();
  };

  if (depotsLoading || bacsLoading) {
    return <div className={styles.loading}>Chargement des dépôts…</div>;
  }

  if (selectedDepotId !== null) {
    const stat = depotStats.find(s => s.depot.Id === selectedDepotId);
    if (!stat) {
      return (
        <div className={styles.empty}>
          Dépôt introuvable.
          <button className={styles.linkBtn} onClick={() => setSelectedDepotId(null)}>
            ← Retour à la liste
          </button>
        </div>
      );
    }
    const depotBacs = bacs.filter(b => b.DepotId === stat.depot.Id);
    const depotBacIds = new Set(depotBacs.map(b => b.Id));
    const depotCapacites = capacites.filter(c => depotBacIds.has(c.BacId));
    return (
      <DepotDetail
        depot={stat.depot}
        bacs={depotBacs}
        planning={entries}
        capacites={depotCapacites}
        isAdmin={isAdmin}
        onBack={() => setSelectedDepotId(null)}
        onChanged={handleAfterEdit}
        kpi={{
          bacsCount: stat.bacsCount,
          volumeTotal: stat.volumeTotal,
          indispoCount: stat.indispoCount,
          currentProducts: stat.currentProducts,
          withRestriction: stat.withRestriction,
        }}
      />
    );
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.toolbar}>
        <div>
          <h2 className={styles.h2}>Dépôts</h2>
          <p className={styles.subtitle}>
            {depots.length} sites &middot; {bacs.length} bacs &middot;{' '}
            {bacs.reduce((s, b) => s + (b.VolumeUtile || 0), 0).toLocaleString('fr-FR')} m³ de capacité totale
          </p>
        </div>
        <input
          className={styles.search}
          placeholder="⌕ Rechercher un dépôt…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {filteredStats.length === 0 ? (
        <div className={styles.empty}>Aucun dépôt correspondant.</div>
      ) : (
        <div className={styles.grid}>
          {filteredStats.map((s, idx) => (
            <DepotCard
              key={s.depot.Id}
              stat={s}
              animDelay={idx * 0.04}
              onClick={() => setSelectedDepotId(s.depot.Id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};
